import { ExternalLink, Newspaper, Tablet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trackGTMEvent } from '@/components/GoogleTagManager';
import { trackEventMp } from '@/components/Mixpanel';
import einkPreview from '@/assets/newsforyou-io-eink.jpg';

export const NEWSFORYOU_IO_URL = 'https://newsforyou.io/?utm_source=newsforyou.ai&utm_medium=referral&utm_campaign=decommissioning&utm_content=shutdown_modal';

interface DecommissioningModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DecommissioningModal({ open, onOpenChange }: DecommissioningModalProps) {
  const { t } = useTranslation();

  const handleVisit = () => {
    trackGTMEvent('decommissioning_modal_cta_clicked', {
      destination: 'newsforyou.io',
      source: 'shutdown_modal',
    });
    trackEventMp('decommissioning_modal_cta_clicked', {
      destination: 'newsforyou.io',
      source: 'shutdown_modal',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto border-border/80 bg-card p-0 shadow-2xl sm:rounded-lg">
        <div className="relative aspect-[5/2] overflow-hidden border-b border-border">
          <img
            src={einkPreview}
            alt={t('decommissioningModal.imageAlt')}
            loading="lazy"
            width={1200}
            height={720}
            className="h-full w-full object-cover"
          />
          <div className="absolute left-4 top-4 flex items-center gap-2 rounded-md border border-border/80 bg-background/90 px-3 py-2 text-xs font-semibold text-foreground backdrop-blur-md">
            <Tablet className="h-4 w-4 text-newsletter-primary" />
            {t('decommissioningModal.badge')}
          </div>
        </div>

        <div className="space-y-6 p-6 sm:p-8">
          <DialogHeader className="space-y-3 text-left">
            <div className="flex h-10 w-10 items-center justify-center rounded-md border border-newsletter-primary/30 bg-newsletter-primary/10">
              <Newspaper className="h-5 w-5 text-newsletter-primary" />
            </div>
            <DialogTitle className="text-2xl leading-tight sm:text-3xl">
              {t('decommissioningModal.title')}
            </DialogTitle>
            <DialogDescription className="text-base leading-relaxed">
              {t('decommissioningModal.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="border-l-2 border-newsletter-primary pl-4">
            <p className="font-semibold text-foreground">{t('decommissioningModal.newProductTitle')}</p>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {t('decommissioningModal.newProductDescription')}
            </p>
          </div>

          <DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t('decommissioningModal.close')}
            </Button>
            <Button asChild variant="premium" className="h-11 px-5">
              <a href={NEWSFORYOU_IO_URL} target="_blank" rel="noreferrer" onClick={handleVisit}>
                {t('decommissioningModal.visit')}
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}